// ChatMessageBubble — Presentational chat message bubble for a single message
// (bot or user), including avatar, bubble content + formatted time, optional
// quick-reply chips, and optional hotel/restaurant cards.
//
// Extracted verbatim from `AIChatBot.js` (the in-file `ChatMessageBubble`
// sub-component) as part of the behavior-preserving refactor (issue #240).
// Render-only, wrapped in React.memo. Markup, styles, avatar gradients, and the
// `message.quickReplies && message.quickReplies.length > 0` render guard are
// identical to the source. The quick-reply chips and the card block are
// delegated to the co-located `QuickReplies` and `MessageCards` memoized
// components, which render the same DOM the source produced inline.
//
// Time is accepted as a `formatTime` function prop (owned by the container /
// hook, backed by `logic/formatMessageTime`) and called as
// `formatTime(message.timestamp)` exactly as the source did — no clock access
// or fetching happens here.
//
// Props:
//   message      - the message object (type, content, timestamp, quickReplies, cards)
//   isLoading    - boolean, disables quick-reply chips while loading
//   onQuickReply - callback invoked with a quick-reply value
//   tripData     - the trip object, passed through to MessageCards
//   formatTime   - (Date) => string formatter for the message timestamp
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Bot, User } from 'lucide-react';
import QuickReplies from './QuickReplies';
import MessageCards from './MessageCards';

// ── Chat message bubble ───────────────────────────────────────────────────────
export const ChatMessageBubble = React.memo(function ChatMessageBubble({ message, isLoading, onQuickReply, tripData, formatTime }) {
  return (
    <div key={message.id} style={{
      display: 'flex',
      justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
      alignItems: 'flex-start',
      gap: '10px'
    }}>
      {message.type === 'bot' && (
        <div style={{
          width: '32px', height: '32px',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginTop: '4px'
        }}>
          <Bot size={16} color="white" />
        </div>
      )}

      <div style={{ maxWidth: '82%' }}>
        {/* Message bubble */}
        <div style={{
          background: message.type === 'user' ? '#667eea' : '#f3f4f6',
          color: message.type === 'user' ? 'white' : '#374151',
          padding: '10px 14px',
          borderRadius: '16px',
          fontSize: '14px',
          lineHeight: '1.5',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}>
          {message.content}
          <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '4px' }}>
            {formatTime(message.timestamp)}
          </div>
        </div>

        {/* Quick reply chips */}
        {message.quickReplies && message.quickReplies.length > 0 && (
          <QuickReplies
            replies={message.quickReplies}
            isLoading={isLoading}
            onQuickReply={onQuickReply}
          />
        )}

        {/* Cards (hotel / restaurant) */}
        <MessageCards cards={message.cards} tripData={tripData} />
      </div>

      {message.type === 'user' && (
        <div style={{
          width: '32px', height: '32px',
          background: '#667eea',
          borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginTop: '4px'
        }}>
          <User size={16} color="white" />
        </div>
      )}
    </div>
  );
});

export default ChatMessageBubble;
